import React, {Component} from 'react'
import GistItem from './GistItem'
import TOCItem from '../toc/TOCItem'
import styled from '@emotion/styled'
import sv from '../../../../styles/styles'
import {cloudRefiner} from "../../../../defaults/cloudMaker";
import {playerConstants} from "../../../../_constants/commons/playerConstants";
import debounce from 'lodash/debounce'
import {__isEmpty} from "../../../../_helpers";

// STYLE ##########################################################

const Container = styled(`div`)`
  position: relative;
  height: 100%;
  width: 100%;
  ${sv.flexColumn};
`;

const CloudRow = styled(`div`)`
  width: 100%;
  flex: 1;
  justify-content: space-around;
  ${sv.flexRow}
`;

// COMPONENT ##########################################################

const canvasFontReplacement = "Arial";  // font-family to be used for canvas based measurements

class MasonryCloud extends Component {

  state = {
    rows: [],             // the rows to be rendered
    rowHeight: 0,         // the height of a row
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.cloud.length !== this.props.cloud.length) {   // perform update iff cloud length changes
      this.layoutItems();
    }
  };

	/**
	 * Static function that measures the width of the given item text (with design properties)
	 *
	 * @param item        the given item that contains the text
	 * @param fontDetails the design added to the text
	 * @returns {number}  the size of text with design
	 */
	static getTextWidth(item, fontDetails) {
		const { createCanvas } = require('canvas');
		const canvas = createCanvas(500, 500);
		const context = canvas.getContext('2d');

		/*
		 * Custom family-fonts may corrupt the measurement if not recognized in the canvas
		 * Using Arial font was a close enough representation of the cloud
		 * under controlled experiment (@see metric.js file)
		 */
		const font = `${fontDetails.font.weight} ${fontDetails.font.size}px ${canvasFontReplacement}`;
		const secondaryFont = `${fontDetails.secondaryFont.weight} ${fontDetails.secondaryFont.size}px ${canvasFontReplacement}`;

		let maxWidth;
		let words = item.name.split(" ");
		if (words.length > 1) {
			context.font = secondaryFont;
			let firstLineMetrics = context.measureText(words.slice(0, -1).join(" "));
			context.font = font;
			let secondLindMetrics = context.measureText(words[words.length - 1]);
			maxWidth = firstLineMetrics.width > secondLindMetrics.width ? firstLineMetrics.width : secondLindMetrics.width;
		} else {
			context.font = font;
			const metrics = context.measureText(item.name);
			maxWidth = metrics.width;
		}

		canvas.remove();
		return maxWidth;
	};

	/**
	 * Finds the optimal state of the layout
	 *
	 * @returns {Promise<void>}
	 */
	layoutItems = debounce( async () => {
		// Memoization : holds the layout with number of cloud items as keys
		let layoutCollection = {};

		// returns from layout-collection (if present), else calculates, stores and returns
		const getLayout = (cloud) => {
			if(layoutCollection.hasOwnProperty(cloud.length)) {             // checks if previously calculated
				return layoutCollection[cloud.length];                        // return stored layout for the cloud
			} else {
				layoutCollection[cloud.length] = this.calculateLayout(cloud); // calculate new layout
				return layoutCollection[cloud.length];                        // return the calculated layout
			}
		};

		/*
		 * Terminating conditions:
		 * 1. High={fit}  , high-cloud is optimal
		 * 2. Mid<{1}     , problem size cannot be reduced further
		 * 3. ReTry={max} , max-retries
		 *
		 * Re-try setup:
		 * 1. High={overflow} && Low={fit}      , then re-try within (Low) and (Low + Mid)
		 * 2. High={overflow} && Low={overflow} , then re-try within (Low - Mid) and (Low)
		 */
		let highCloud = this.props.cloud;                                       // original cloud as high-cloud
		let lowCloud = cloudRefiner(highCloud, highCloud.length/2);   // cloud halved
		let maxTries = 10;                                                      // max number of tries

		for (let i = 0; i < maxTries; i++) {                                    // until the best fit is found or max-retries

			const highCloudLayout = getLayout(highCloud);
			const highVerify = highCloudLayout.isVerified;

			if (highVerify) {                                                     // found the most optimal layout
				break;

			} else {

				const lowCloudLayout = getLayout(lowCloud);
				const lowVerify = lowCloudLayout.isVerified;

				const mid = (highCloud.length - lowCloud.length) / 2;               // mid of length of two compared cloud

				if (mid < 1) {                                                      // cannot decrease problem size anymore
					break;

				} else if (i === maxTries - 1) {                                    // max retries used, fallback to highCloudLayout
					break;

				} else if (!highVerify && lowVerify) {                              // re-try within [lowCloud.length , ... , highCloud.length]
					// update low-cloud, high-cloud remains same
					const increasedLowProbe = mid + lowCloud.length;
					lowCloud = cloudRefiner(highCloud, increasedLowProbe);

				} else if (!lowVerify) {                                            // re-try within [0, ... ,lowCloud.length]
					// swap high-cloud with current low-cloud and update low-cloud
					const newHighCloud = lowCloud;
					const reducedLowProbe = lowCloud.length - mid;
					lowCloud = cloudRefiner(lowCloud, reducedLowProbe);
					highCloud = newHighCloud;
				}
			}
		}

		let optimalLayout;  // holds the verified-layout that has largest number of items
		for(const layout in layoutCollection) {
			if(!layoutCollection[layout].isVerified) break;
			optimalLayout = layoutCollection[layout];
		}

		// if single-column, add forced margins for cloudy look
		this.addForcedPaddingIfSingleOrDoubleColumnLayout(optimalLayout);

		this.finalizeOptimalLayout(optimalLayout);
	}, 300, {leading: true, maxWait: 1000});

	/**
	 * Calculates the layout parameters for the given cloud
	 *
	 * @param cloud the cloud to be mapped to the available space
	 * @returns {{itemRows: [][], rowWidths: any[], rows: [][], rowHeight: number}}
	 */
	calculateLayout (cloud) {
		let maxRows = [[], [], [], [], []];
		let rowCount = this.props.zoomLevel === 2 ? 5 : 3;
		let rows = maxRows.slice(0, rowCount);
		let itemRows = [[], [], [], [], []];
		let calculatedRowHeight = this.props.parentHeight / rowCount;

		const isSmallScreen = this.props.chapterWidth < playerConstants.CLOUD_LAYOUT_MAX_WIDTH.SMALL_SCREEN;
		const uniformPadding = 16;                          // padding space irrespective of screen sizes (alleviates multi-word extra padding)
		const extraPadding = (isSmallScreen) ? 8 : 25;     // allow more elements to fit on smaller screens and toc
		const totalPadding = uniformPadding + extraPadding;

		// set max row height at 65 to avoid layout inconsistencies when corresponding items are far apart (when the height are changed)
		let rowHeight = (calculatedRowHeight < 65) ? calculatedRowHeight : 65;
		let rowWidths = new Array(rowCount).fill(0);

		// first build array of items and log widths for fit verification
		cloud.forEach((item, index) => {
			const rowTarget = rowWidths.indexOf(Math.min.apply(Math, rowWidths));       // use the row with the most available space
			const fontAndPaddingInfo = this.getFontSizeAndPadding(item, rowHeight);     // get item design information
			const itemTextWidth = MasonryCloud.getTextWidth(item, fontAndPaddingInfo);  // measure item width
			const itemWidth = itemTextWidth + (totalPadding * 2);                       // add additional padding spaces

			// build a new object
			const newItem = Object.assign({}, item, {rowIndex: index, itemWidth: itemWidth}, fontAndPaddingInfo );
			itemRows[rowTarget].push(newItem);  // push the object to the associated row
			rowWidths[rowTarget] += itemWidth   // log widths of associated row
		});

		return {
			"itemRows": itemRows,
			"rowWidths": rowWidths,
			"isVerified": this.verifyLayout(rowWidths),
			"rows": rows,
			"rowHeight": rowHeight,
		};
	};

	/**
	 * Verifies the layout by checking row-widths overflow
	 *
	 * @param rowWidths given row-width array
	 * @returns {boolean}
	 */
	verifyLayout (rowWidths) {
		const overflow = (width) => width > this.props.chapterWidth;
		return !rowWidths.some(overflow);
	};

	/**
	 * It accepts a (single-column) layout and updates the padding values for placement randomization
	 *
	 * @param layout a single-column layout
	 */
	addForcedPaddingIfSingleOrDoubleColumnLayout(layout) {

		// if single- or double-column chapter then update the padding (for randomization), else return as-it-is
		if ( layout.itemRows.some((arr) => arr.length > 2) ) return;

		const paddingSpaceAvailability = layout.rowWidths.map((itemSpace, index) => {
			const availableSpaceInCurrentRow = this.props.chapterWidth - itemSpace;	// actual space available in row
			const spaceAreaCount = layout.itemRows[index].length + 1;				// areas where available space could be utilized
			return availableSpaceInCurrentRow / spaceAreaCount;
		});
		const layoutRand = this.hashes(layout.itemRows[0][0])[0];					//pseudo-random consistent number for this layout

		/*
		 * List of Operations:
		 * 1. Left-Move-Operation   : decrease margin as available space
		 * 2. Right-Move-Operation  : increase margin as available space
		 * 3. No-Move-Operation     : move 25% of available space in random (left/right)
		 */
		const leftOperation = (currentP, deductP) => currentP - deductP;
		const rightOperation = (currentP, addP) => currentP + addP;
		const stayOperation = (currentP, movableP) => (layoutRand>=0.5) ? (currentP -(movableP * 0.25)) : (currentP + (movableP * 0.25));

		for(let index=0; index<layout.itemRows.length ; index++) {			// index increases by length of operations on each iteration
			if(__isEmpty(layout.itemRows[index])) continue;					// if empty item row then continue

			const rowRands = this.hashes(layout.itemRows[index][0]);		// array of two pseudo-random consistent numbers for this row

			/*
			 * Randomize alignment operations
			 */
			// Left and right alignments should happen together
			const finalizedInterRandomized = rowRands[0] < 0.5 ? [leftOperation, rightOperation] : [rightOperation, leftOperation];
			// Stay alignment should always be at either the beginning or end, otherwise it looks like a diagonal line
			const randomizedOperationCategories = rowRands[1] < 0.5 ? [stayOperation, finalizedInterRandomized] : [finalizedInterRandomized, stayOperation];
			const finalOperationsList = randomizedOperationCategories.flat();

			// apply an operation cycle
			for(let opIndex=0; opIndex<finalOperationsList.length; opIndex++, index++) {
				if(index >= layout.itemRows.length) break;                          // exit if padding operation assigned to all rows

				const currentPadding = layout.itemRows[index][0].padding.left;      // current padding
				const remainingSpace = paddingSpaceAvailability[index];             // space available that can be used
				layout.itemRows[index][0].padding.left = finalOperationsList[opIndex](currentPadding, remainingSpace);
			}
		}
	};

  /**
   * Hashes and item returning a somewhat random, yet consistent array of two values between 0 and 1.
   *
   * Having a seemingly-random value that is always the same for a given word makes for consistent display.
   */
  hashes = (item) => {
    let forwardHash = 0;
    let backwardHash = 0;

    let forwardChar, backwardChar;

    for (let i=0; i<item.name.length; i++) {
      forwardChar = item.name.charCodeAt(i);
      backwardChar = item.name.charCodeAt(item.name.length - 1 - i);

      forwardHash = ((forwardHash<<5)-forwardHash)+forwardChar;
      forwardHash = forwardHash & forwardHash;            // keeps it to a 32-bit integer

      backwardHash = ((backwardHash<<5)-backwardHash)+backwardChar;
      backwardHash = backwardHash & backwardHash;         // keeps it to a 32-bit integer
    }

    forwardHash = (forwardHash / (2147483647) + 1)/2;     // bounds it to be between 0 and 1
    backwardHash = (backwardHash / (2147483647) + 1)/2;   // bounds it to be between 0 and 1

    return [forwardHash, backwardHash];
  };

	/**
	 * Prepares the design setup for the given item
	 *
	 * @param item      the given item
	 * @param rowHeight the available row height
	 * @returns {{padding: {top: number, left: number}, secondaryFont: {size: number, color: string, weight: number}, font: {size: number, color: string, weight: number}}}
	 */
	getFontSizeAndPadding = (item, rowHeight) => {
		let isNarrowScreen = this.props.chapterWidth < playerConstants.CLOUD_LAYOUT_MAX_WIDTH.SMALL_SCREEN;
		const fontWeight = 700 - (item.tier - 1) * 84;          // [700, 616, 532, 448]
		const secondaryFontWeight = 275 - (item.tier - 1) * 25; // [275, 250, 225, 200]

		const fontScale = Math.floor(255 / (Math.sqrt(item.tier + 6) - 1.64575));
		const fontColor = `rgba(${fontScale},${fontScale},${fontScale},1.0)`;

		const secondaryFontScale = Math.floor(255 / (0.12 * item.tier + 1.05));
		const secondaryFontColor = `rgba(${secondaryFontScale},${secondaryFontScale},${secondaryFontScale},1.0)`;

		const fontSizes = this.props.isTableOfContents || isNarrowScreen ? [19, 16, 14, 12] : [24, 20, 16, 13];
		const secondaryFontSizes = this.props.isTableOfContents || isNarrowScreen ? [14, 12, 11, 10] : [18, 15, 13, 11];
		let fontSize = fontSizes[item.tier - 1];
		let left, top;
		let hasLineBreak = item.name.split(" ").length > 1;
		const availableVertical = (rowHeight - (hasLineBreak ? 2 : 1) * fontSize) - 4;
		const availableHorizontal = 28;

		let rands = this.hashes(item);
		top = Math.floor(rands[0] * availableVertical) + 4;
		left = Math.floor(rands[1] * availableHorizontal) + 1;

		const padding = {left: left, top: top};
		const font = {
			size: fontSize,
			weight: fontWeight,
			color: fontColor,
		};
		const secondaryFont = {
			size: secondaryFontSizes[item.tier - 1],
			weight: secondaryFontWeight,
			color: secondaryFontColor
		};
		return {padding: padding, font: font, secondaryFont: secondaryFont};
	};

	/**
	 * Finalizes the optimal layout into corresponding rows for output emission
	 *
	 * @param optimalLayout the most optimal layout based on available space
	 */
	finalizeOptimalLayout(optimalLayout) {
		optimalLayout.itemRows.forEach((row, mapRowIndex) => {
			row.forEach((item, itemIndex) => {
				// Now fill the array of components that we'll render.
				if (this.props.isTableOfContents) {
					optimalLayout.rows[mapRowIndex].push(
						<TOCItem
							key={itemIndex}
							item={item}
						/>
					)
				} else {
					optimalLayout.rows[mapRowIndex].push(
						<GistItem
							key={itemIndex}
							item={item}
							height={optimalLayout.rowHeight}
							playWord={this.props.playWord}
							showWordOnTimeline={this.props.showWordOnTimeline}
							hideWordOnTimeline={this.props.hideWordOnTimeline}
							onHintTextShown={this.props.onHintTextShown}
							chapterWidth={this.props.chapterWidth}
							getGistPlayerRect={this.props.getGistPlayerRect}
							setLockGist={this.props.setLockGist}
						/>
					)
				}
			})
		});

		this.setState({
			rowHeight: optimalLayout.rowHeight,
			rows: optimalLayout.rows
		});
	}

  render() {
    return (
      <Container>
        {this.state.rows.map((row, index) => (
            <CloudRow key={index}>
              {row}
            </CloudRow>
        ))}
      </Container>
    )
  }
}

export default MasonryCloud
